/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.14.0
 * @since 1.13.0
 */

import { useState } from 'react';

import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { BadgeStatus, LinkBlock } from '../../../../../components/common';
import { EBadgeStatus } from '../../../../../components/common/Badge/BadgeStatus';
import { ELocaleKeys } from '../../../../../modules/internationalization/intConstants';
import { formatDate } from '../../../../../utils/formatText';
import { stringFormatParams } from '../../../../../utils/stringFormat';
import { EFormatData } from '../../../../PageDashboard/types/widgetTypes';
import { useSubscriberIdContext } from '../../../context';
import SubscriberIdEmpty from '../../SubscriberIdEmpty';
import { dcRuleListSelector, dcRulesTranslationSelector } from '../selectors';


const SubscriberIdDcRuleList = () => {

    const { subscriberId } = useSubscriberIdContext();

    const translationTab = useSelector(dcRulesTranslationSelector());
    const rules = useSelector(dcRuleListSelector(subscriberId));

    const [isFullShow, setFullShow] = useState(!rules.find(rule => rule.isActive));


    const renderDate = (date: string, text: string, isActive?: boolean) => (
        <div className={classNames('subscriber-dc-rules__rule-date', {
            'subscriber-dc-rules__rule-date_active': isActive,
        })}>
            <div className="rule-date__time">{formatDate(date)}</div>
            {text}
        </div>
    );

    if (!rules.length) return <SubscriberIdEmpty left withoutImage title={translationTab.empty}/>;

    return (
        <div className="subscriber-dc-rules__block">
            <div className="subscriber-id__title subscriber-id__title_bold" style={{ marginBottom: 16 }}>
                {stringFormatParams(translationTab.rulesList.title, {
                    [ELocaleKeys.number]: rules.length,
                })}
            </div>
            {
                rules.filter(rule => (rule.isActive || isFullShow)).map(rule => (
                    <div className="subscriber-dc-rules__rule">

                        <div className="subscriber-dc-rules__rule-header">
                            <div className="subscriber-id__title subscriber-id__title_bold">{rule.name}</div>
                            {
                                rule.dateStart && (
                                    <BadgeStatus
                                        className="subscriber-dc-rules__date"
                                        status={rule.isActive ? EBadgeStatus.active : EBadgeStatus.future}
                                        text={rule.isActive
                                            ? translationTab.rulesList.dateStartAlready
                                            : stringFormatParams(translationTab.rulesList.dateStart, {
                                                [ELocaleKeys.date]: formatDate(rule.dateStart, {
                                                    format: EFormatData.FullData,
                                                    skipZone: true,
                                                }),
                                            })}
                                    />
                                )
                            }
                        </div>

                        {
                            rule.dateStart && renderDate(
                                rule.dateStart,
                                rule.isActive
                                    ? translationTab.rulesList.period.dateStartAlready
                                    : translationTab.rulesList.period.dateStart,
                                rule.isActive,
                            )
                        }
                        {
                            rule.dateChange?.map(date => renderDate(date, translationTab.rulesList.period.dateChange))
                        }
                        {
                            rule.dateEnd && renderDate(rule.dateEnd, translationTab.rulesList.period.dateEnd)
                        }
                    </div>
                ))
            }
            {
                rules.length > 1 && (
                    <LinkBlock onClick={() => setFullShow(!isFullShow)}>
                        {
                            stringFormatParams(isFullShow
                                    ? translationTab.rulesList.showShort
                                    : translationTab.rulesList.showAll,
                                { [ELocaleKeys.number]: rules.filter(rule => !rule.isActive).length })
                        }
                    </LinkBlock>
                )
            }
        </div>
    );
};

export default SubscriberIdDcRuleList;
